// React
import type {
	AnchorHTMLAttributes,
	ButtonHTMLAttributes,
	DetailedHTMLProps,
	ReactNode,
} from "react"

// Router
import { Link, LinkProps, type To } from "@/lib/router"

// UI
import { cn } from "@/lib/utils"

// Types
export type ButtonVariants = keyof typeof VARIANT_CLASSES
export type ButtonSizes = keyof typeof SIZE_CLASSES

// Django's Tailgrids pill uses no border/shadow and rounded-lg; the SPA's
// `primary` variant is aligned to that style so the marketing header and
// the app buttons stay visually consistent.
const VARIANT_CLASSES = {
	primary: `bg-primary-500 hover:bg-secondary-500 focus:ring-primary-500 text-dark hover:text-white border-0 rounded-lg shadow-none`,
	secondary: `bg-secondary-500 hover:bg-secondary-700 hover:border-secondary-700 focus:ring-secondary-500 text-white`,
	disabled: `bg-gray-200 text-black border-gray-200`,
	transparent: `bg-white hover:bg-gray-50 focus:ring-primary-500 border-gray-200 text-gray-500 hover:text-gray-900 text-dark`,
	empty: `bg-transparent border-0 text-gray-500 hover:text-gray-900 shadow-none text-dark`,
	error: `bg-red-600 hover:bg-red-700 focus:ring-red-600 text-white`,
	dark: `bg-gray-700 hover:bg-black focus:ring-gray-800 text-white`,
	// Django's light-gray Signup pill (`bg-gray-2` in Tailgrids ≈ #F4F7FF).
	// Only used in the marketing/unauth header today.
	"pill-secondary": `bg-[#F4F7FF] hover:text-secondary-500 focus:ring-primary-500 text-dark border-0 rounded-lg shadow-none`,
}

const DISABLED_CLASSES: {
	[key in ButtonVariants | "disabled"]?: string
} = {
	disabled: `bg-gray-300 hover:bg-gray-300 focus:ring-gray-300 border-gray-300`,
	empty: `bg-transparent focus:ring-gray-300 text-gray-300 shadow-none`,
}

const SIZE_CLASSES = {
	regular: "px-4 py-2 text-base",
	small: "px-4 py-2 text-sm",
	xsmall: "px-4 py-2 text-xs",
	xxsmall: "px-2 py-2 text-xs",
	// Django's Tailgrids call-to-action pill (rounded-lg + px-5 + py-2.5).
	pill: "px-5 py-2.5 text-base",
}

const getClassName = ({
	disabled,
	variant,
	className,
}: {
	disabled?: boolean
	variant: ButtonVariants
	size: ButtonSizes
	className?: string
}) => {
	// if it's disabled, we check if there are disabled class names
	// otherwise we grab the variant
	const styleClass = disabled
		? (DISABLED_CLASSES[variant] ?? DISABLED_CLASSES.disabled)
		: VARIANT_CLASSES[variant]

	const classNameRoot = cn(
		"overflow-hidden",
		"inline-flex",
		"border",
		"rounded-md shadow-xs",
		"font-medium",
		"transition",
		"focus:outline-hidden focus:ring-2 focus:ring-offset-2",
		styleClass,
	)

	return cn(className, classNameRoot)
}

export interface ButtonProps
	extends Omit<
		DetailedHTMLProps<
			ButtonHTMLAttributes<HTMLButtonElement>,
			HTMLButtonElement
		>,
		"className" | "ref" | "size"
	> {
	variant?: ButtonVariants
	size?: ButtonSizes
	className?: string
	componentRight?: ReactNode
}

/**
 * Button
 * @param param0
 * @returns
 */
export const Button = ({
	componentRight,
	className = "",
	variant = "primary",
	size = "regular",
	children,
	...props
}: ButtonProps) => {
	const classNameRoot = getClassName({
		disabled: props.disabled,
		variant,
		size,
		className,
	})

	return (
		<button className={classNameRoot} {...props}>
			{/** Button content */}
			<div
				className={cn(
					"inline-flex items-center justify-center",
					SIZE_CLASSES[size],
				)}
			>
				{children}
			</div>

			{/** Render component inside button on the right */}
			{componentRight !== undefined && <>{componentRight}</>}
		</button>
	)
}

interface ButtonLinkProps
	extends Omit<LinkProps, "className" | "to" | "href" | "target" | "rel">,
		Pick<
			AnchorHTMLAttributes<HTMLAnchorElement>,
			"href" | "target" | "rel"
		> {
	variant?: ButtonVariants
	size?: ButtonSizes
	className?: string
	disabled?: boolean
	componentRight?: ReactNode
	to?: To
	/**
	 * External URL. When set, renders a plain <a> instead of react-router's
	 * <Link>, so navigation goes to the target host (e.g. the Django backend)
	 * with a full page load.
	 */
	href?: string
}

export const ButtonLink = ({
	className = "",
	variant = "primary",
	size = "regular",
	children,
	componentRight,
	to = "",
	href,
	target,
	rel,
	...props
}: ButtonLinkProps) => {
	const classNameRoot = getClassName({
		disabled: props.disabled,
		variant,
		size,
		className,
	})

	const inner = (
		<>
			<div
				className={cn(
					"inline-flex items-center justify-center",
					SIZE_CLASSES[size],
				)}
			>
				{children}
			</div>
			{componentRight !== undefined && <>{componentRight}</>}
		</>
	)

	if (href) {
		// Default `rel` on cross-origin targets to prevent reverse-tabnabbing
		// (a `target="_blank"` link with no rel leaks window.opener).
		const safeRel =
			target === "_blank" ? (rel ?? "noopener noreferrer") : rel
		return (
			<a
				{...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
				className={classNameRoot}
				href={props.disabled ? undefined : href}
				target={target}
				rel={safeRel}
				aria-disabled={props.disabled ?? undefined}
			>
				{inner}
			</a>
		)
	}

	return (
		<Link className={classNameRoot} to={to} {...props}>
			{inner}
		</Link>
	)
}

export interface SubmitButtonProps extends ButtonProps {
	className?: string
	isSubmitting?: boolean
	disableWhenPristine?: boolean
}

export const SubmitButton = ({
	className,
	children,
	isSubmitting,
	...props
}: SubmitButtonProps) => (
	<Button
		type="submit"
		className={cn(className, isSubmitting && "relative overflow-hidden")}
		{...props}
	>
		{isSubmitting ? (
			<>
				<span className="relative z-10">{children}</span>
				<ProgressIndicator />
			</>
		) : (
			children
		)}
	</Button>
)

const ProgressIndicator = () => (
	<div
		data-testid="progress-indicator"
		className="animate-conveyor-slow pointer-events-none absolute top-0 left-0 h-full"
		style={{
			width: `calc(100% + 40px)`,
			background: `repeating-linear-gradient(
				45deg,
				rgba(255,255,255,0.5),
				rgba(255,255,255,0.5) 10px,
				rgba(255,255,255,0) 10px,
				rgba(255,255,255,0) 20px
			)`,
		}}
	/>
)
